import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
  useLocationFilter,
} from 'src/components/Filters';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { EventDateSelectorV2 } from 'src/core/POS/DateRangeSelector/EventDateSelectorV2';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { useParentCategoryToCid } from 'src/hooks/useParentCategoryToCid';
import { ContentId } from 'src/utils/constants/contentId';
import { TOP_LEVEL_CATEGORY_TO_CID } from 'src/utils/constants/contentIdMaps';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import {
  EntityWithTicketsQuery,
  EventTimeFrameFilter,
  TopLevelCategory,
} from 'src/WebApiController';

import { EventSelector } from '../Selectors/EventSelector';
import { PerformerSelector } from '../Selectors/PerformerSelector';
import { VenueSelector } from '../Selectors/VenueSelector';
import { useEventStateFilters } from './useEventStateFilters';

export type CommonEventFilter = {
  eventTimeFrameFilter: null;
  eventDates: null;
  countryCodes: null;
  stateProvinceIds: null;
  cityIds: null;
  topLevelCategories: null;
  parentCategories: null;
  eventStatuses: null;

  // there are only for the search-views
  eventOrMappingIds: null;
  performerIds: null;
  venueIds: null;
};

export type CommonEventFiltersProps = {
  showCatalogFilters?: boolean;
  showEventFilter?: boolean;
  showVenueFilter?: boolean;
  showPerformerFilter?: boolean;
  firstRenderOpen?: boolean;
  additionalFilters?: FilterToolbarItem[];
  forceUseCatalogV2?: boolean;
};

export const useCommonEventFilters = ({
  showCatalogFilters,
  showEventFilter = true,
  showVenueFilter,
  showPerformerFilter,
  additionalFilters,
  firstRenderOpen,
  forceUseCatalogV2,
}: CommonEventFiltersProps): FilterToolbarGroup => {
  const { tempQuery, initialQuery, setTempQuery } = useFilterQueryContext<
    EntityWithTicketsQuery & QueryWithViewMode
  >();

  const locationFilters = useLocationFilter({});
  const eventStateFilters = useEventStateFilters({});
  const {
    map: parentCategoryToCid,
    groupedConfig: parentCategoryGroupedConfig,
  } = useParentCategoryToCid();

  const hideCatalogFilters = !showCatalogFilters;

  return {
    titleContentId: ContentId.Events,
    items: [
      {
        filterId: 'eventDates' as FilterToolbarItemId,
        labelContentId: ContentId.EventDate,
        filterQueryKeys: [
          'eventDates',
          'eventTimeFrameFilter',
        ] as FilterToolbarItemId[],
        filterItem: (
          <EventDateSelectorV2
            eventTimeFrame={tempQuery.eventTimeFrameFilter}
            value={tempQuery.eventDates}
            onChange={({ value: dateRange, eventTimeFrameFilter }) => {
              if (
                eventTimeFrameFilter !== tempQuery.eventTimeFrameFilter ||
                !isEqual(dateRange, tempQuery.eventDates)
              ) {
                let isSortDescending = tempQuery.isSortDescending;
                if (eventTimeFrameFilter !== EventTimeFrameFilter.Future) {
                  // When sorting by past or by all - we do not want to sort by date asc (as that would put the oldest event first)
                  // Changing this to desc
                  isSortDescending = true;
                } else {
                  isSortDescending = initialQuery.isSortDescending;
                }
                setTempQuery({
                  ...tempQuery,
                  eventTimeFrameFilter: eventTimeFrameFilter ?? null,
                  eventDates: dateRange ?? null,
                  isSortDescending,
                });
              }
            }}
          />
        ),
      },
      ...(hideCatalogFilters
        ? []
        : [
            ...(showEventFilter
              ? [
                  {
                    filterId: 'eventOrMappingIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Event,
                    filterQueryKeys: [
                      'eventOrMappingIds',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <EventSelector
                        firstRenderOpen={firstRenderOpen}
                        values={tempQuery.eventOrMappingIds ?? []}
                        eventTimeFrameFilter={tempQuery.eventTimeFrameFilter}
                        eventDatesFilter={tempQuery.eventDates}
                        placeholderText={ContentId.SearchAndSelectAnEvent}
                        onChange={({ newValues }) => {
                          if (
                            !isEqual(newValues, tempQuery.eventOrMappingIds)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              eventOrMappingIds: newValues ?? null,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
            ...(showVenueFilter
              ? [
                  {
                    filterId: 'venueIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Venue,
                    filterQueryKeys: ['venueIds'] as FilterToolbarItemId[],
                    filterItem: (
                      <VenueSelector
                        firstRenderOpen={firstRenderOpen}
                        triggerProps={{
                          style: { width: '100%' },
                        }}
                        values={
                          tempQuery.venueIds?.map((vid) => vid.toString()) ?? []
                        }
                        placeholderText={ContentId.SearchAndSelectAVenue}
                        onChange={({ newValues }) => {
                          const valuesAsNumber = newValues?.map((v) =>
                            Number(v)
                          );

                          if (!isEqual(valuesAsNumber, tempQuery.venueIds)) {
                            setTempQuery({
                              ...tempQuery,
                              venueIds: valuesAsNumber ?? null,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
            ...(showPerformerFilter
              ? [
                  {
                    filterId: 'performerIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Performer,
                    filterQueryKeys: ['performerIds'] as FilterToolbarItemId[],
                    filterItem: (
                      <PerformerSelector
                        firstRenderOpen={firstRenderOpen}
                        triggerProps={{
                          style: { width: '100%' },
                        }}
                        values={
                          tempQuery.performerIds?.map((pid) =>
                            pid.toString()
                          ) ?? []
                        }
                        placeholderText={ContentId.All}
                        onChange={({ newValues }) => {
                          const valuesAsNumber = newValues?.map((v) =>
                            Number(v)
                          );
                          if (
                            !isEqual(valuesAsNumber, tempQuery.performerIds)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              performerIds: valuesAsNumber ?? null,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
          ]),
      ...locationFilters,
      {
        filterId: 'topLevelCategories' as FilterToolbarItemId,
        filterQueryKeys: ['topLevelCategories'] as FilterToolbarItemId[],
        labelContentId: ContentId.Genre,
        filterItem: (
          <PosEnumMultiSelect
            triggerProps={{ style: { width: '100%' } }}
            values={
              // TODO: set up enum type for this prop
              (tempQuery.topLevelCategories as TopLevelCategory[]) || []
            }
            placeholderText={ContentId.AllGenres}
            enableEmptySelection
            onChange={({ newValues: topLevelCategories }) => {
              const isChanged = !isEqual(
                topLevelCategories,
                tempQuery.topLevelCategories
              );

              setTempQuery({
                ...tempQuery,
                topLevelCategories: isChanged ? topLevelCategories : [],
              });
            }}
            valueOptionsContent={TOP_LEVEL_CATEGORY_TO_CID}
          />
        ),
      },
      {
        filterId: 'parentCategories' as FilterToolbarItemId,
        labelContentId: ContentId.SubGenre,
        filterQueryKeys: ['parentCategories'] as FilterToolbarItemId[],
        filterItem: (
          <PosMultiSelect
            triggerProps={{ style: { width: '100%' } }}
            values={
              tempQuery.parentCategories
                ? tempQuery.parentCategories.map((c) => String(c))
                : []
            }
            onChange={({ newValues }) => {
              setTempQuery({
                ...tempQuery,
                parentCategories:
                  newValues.length > 0
                    ? newValues.map((value) => Number(value))
                    : null,
              });
            }}
            searchable={true}
            valueOptionsContent={parentCategoryToCid}
            groupedOptionsConfig={parentCategoryGroupedConfig}
          />
        ),
      },
      ...eventStateFilters,
      ...(additionalFilters ?? []),
    ],
  };
};
